import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import classnames from 'classnames';
import {
  ChevronDown,
  ChevronRight,
  Building,
  PeopleFill,
  EnvelopeFill,
  ShieldLock
} from 'react-bootstrap-icons';

import { useAppNavigate } from 'hooks';
import { useGetAuthOverloginUsers, usePostOverLogin } from 'api';
import { IOverloginUser, IOverloginUserListItem } from 'api/types';
import { useDispatch, reduxActions, useSelector } from 'store';
import { SearchInput } from 'components/controls';
import WiseModal from 'components/wise/WiseModal';

import headerStyles from 'theme/components/layouts/MainLayout/components/HeaderTopBar/HeaderTopBar.module.scss';
import styles from 'theme/components/controls/OverLoginUsers/OverLoginUsers.module.scss';

const getInitials = (name?: string | null): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  return (parts[0]?.[0] || '?').toUpperCase();
};

const roleLabel = (
  role: IOverloginUser['company_role'],
  t: (k: string) => string
): string | null => {
  if (role === 'manager') return t('Menadżer');
  if (role === 'buyer') return t('Zamawiający');
  if (role === 'viewer') return t('Podgląd');
  if (role === 'admin') return t('Administrator');
  return null;
};

const OverLoginUsers = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { profile } = useSelector((state) => state.auth);
  const navigate = useAppNavigate();
  const { pathname } = useLocation();

  // parametry zapytania o listę użytkowników
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 10,
    searchKeyword: ''
  });

  // czy widoczny jest modal z przelogowaniem
  const [isOverLoginModal, setIsOverLoginModal] = useState(false);

  // expanded clients (mapa client.id -> bool)
  const [expandedClients, setExpandedClients] = useState<Record<number, boolean>>({});

  // pobranie listy użytkowników możliwych do przelogownania
  const { data: overloginUsersData, refetch: refetchOverloginUsers, isLoading } =
    useGetAuthOverloginUsers(queryParams, { enabled: false });

  // przelogowanie na wybranego użytkownika
  const { mutateAsync: postOverLogin, isLoading: isOverLoginPending } = usePostOverLogin();

  useEffect(() => {
    if (profile?.role !== 'ROLE_OPEN_PROFILE') {
      refetchOverloginUsers();
    }
  }, [queryParams]);

  useEffect(() => {
    if (!isOverLoginModal) {
      setQueryParams({ searchKeyword: '', page: 1, limit: 10 });
      setExpandedClients({});
      return;
    }
    // Trigger fetch gdy modal sie otwiera (queryParams sie nie zmienil)
    if (profile?.role !== 'ROLE_OPEN_PROFILE') {
      refetchOverloginUsers();
    }
  }, [isOverLoginModal]);

  const handleOverLogin = async (id: number) => {
    if (isOverLoginPending) return;
    await postOverLogin({ to_switch_user_id: id });

    if (pathname.includes('/cart/')) {
      navigate('/cart/0');
    }

    dispatch(reduxActions.setCurrentCartId(null));
    dispatch(reduxActions.setOverlogin(id));
    setIsOverLoginModal(false);
  };

  const items = overloginUsersData?.items || [];
  const totalPages = overloginUsersData?.total_pages || 1;

  const totalUsers = useMemo(
    () => items.reduce((acc, c) => acc + c.users_list.length, 0),
    [items]
  );

  const toggleClient = (clientId: number) => {
    setExpandedClients((prev) => ({ ...prev, [clientId]: !prev[clientId] }));
  };

  return (
    <div className={headerStyles.overloginWrapper}>
      <button
        className={headerStyles.overloginButton}
        onClick={() => setIsOverLoginModal(true)}>
        <Trans>Zaloguj jako</Trans>
        <ChevronDown />
      </button>

      <WiseModal
        open={isOverLoginModal}
        onClose={() => setIsOverLoginModal(false)}
        title={t('Zaloguj jako')}
        maxWidth={680}>
        <div className={styles.modalBody}>
          {/* Search */}
          <div className={styles.searchWrapper}>
            <SearchInput
              placeholder={`${t('Szukaj klienta lub użytkownika')}...`}
              value={queryParams.searchKeyword}
              onChange={(value) =>
                setQueryParams(() => ({ searchKeyword: value, page: 1, limit: 10 }))
              }
            />
            {!!queryParams.searchKeyword && (
              <button
                type="button"
                className={styles.clearBtn}
                onClick={() =>
                  setQueryParams(() => ({ searchKeyword: '', page: 1, limit: 10 }))
                }>
                <Trans>Wyczyść</Trans>
              </button>
            )}
          </div>

          {/* Stats */}
          {!isLoading && items.length > 0 && (
            <div className={styles.statsBar}>
              <span className={styles.statPill}>
                <Building /> {items.length}{' '}
                {items.length === 1 ? <Trans>firma</Trans> : <Trans>firm</Trans>}
              </span>
              <span className={styles.statPill}>
                <PeopleFill /> {totalUsers} <Trans>użytkowników</Trans>
              </span>
            </div>
          )}

          {/* Lista klientow */}
          <div className={styles.clientList}>
            {isLoading && (
              <div className={styles.emptyState}>
                <Trans>Ładowanie</Trans>...
              </div>
            )}

            {!isLoading && items.length === 0 && (
              <div className={styles.emptyState}>
                <PeopleFill />
                <span>
                  <Trans>Brak użytkowników do wyświetlenia</Trans>
                </span>
              </div>
            )}

            {!isLoading &&
              items.map((client: IOverloginUserListItem) => {
                // Default: rozwinięte. Użytkownik moze zwinac/rozwinac klikajac w header.
                const isExpanded = expandedClients[client.id] ?? true;
                return (
                  <div key={client.id} className={styles.clientCard}>
                    <div
                      role="button"
                      tabIndex={0}
                      className={styles.clientHeader}
                      onClick={() => toggleClient(client.id)}>
                      <div className={styles.clientIcon}>
                        <Building />
                      </div>
                      <div className={styles.clientBody}>
                        <div className={styles.clientName}>{client.name}</div>
                        <div className={styles.clientId}>
                          <Trans>ID</Trans>: {client.id}
                        </div>
                      </div>
                      <div className={styles.clientCountBadge}>{client.users_list.length}</div>
                      <div
                        className={classnames(styles.chevron, {
                          [styles.chevronOpen]: isExpanded
                        })}>
                        <ChevronRight />
                      </div>
                    </div>

                    {isExpanded && (
                      <div className={styles.usersList}>
                        {client.users_list.map((user) => {
                          const label = roleLabel(user.company_role, t);
                          return (
                            <button
                              key={user.id}
                              type="button"
                              className={styles.userRow}
                              onClick={() => handleOverLogin(user.id)}
                              disabled={isOverLoginPending}>
                              <div className={styles.userAvatar}>
                                {getInitials(user.name)}
                              </div>
                              <div className={styles.userInfo}>
                                <div className={styles.userName}>{user.name}</div>
                                <div className={styles.userLogin}>
                                  <EnvelopeFill /> {user.login}
                                </div>
                              </div>
                              <div className={styles.badgesWrapper}>
                                {user.is_main_user && (
                                  <span className={styles.mainUserBadge}>
                                    <ShieldLock /> <Trans>Admin firmy</Trans>
                                  </span>
                                )}
                                {label && (
                                  <span
                                    className={classnames(styles.rolePill, {
                                      [styles.rolePillManager]:
                                        user.company_role === 'manager',
                                      [styles.rolePillBuyer]:
                                        user.company_role === 'buyer',
                                      [styles.rolePillViewer]:
                                        user.company_role === 'viewer',
                                      [styles.rolePillAdmin]:
                                        user.company_role === 'admin'
                                    })}>
                                    {label}
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          {/* Pagination */}
          {!isLoading && totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={queryParams.page <= 1}
                onClick={() =>
                  setQueryParams((prev) => ({ ...prev, page: prev.page - 1 }))
                }>
                <Trans>Poprzednia</Trans>
              </button>
              <span className={styles.pageInfo}>
                <Trans>Strona</Trans> <strong>{queryParams.page}</strong> / {totalPages}
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={queryParams.page >= totalPages}
                onClick={() =>
                  setQueryParams((prev) => ({ ...prev, page: prev.page + 1 }))
                }>
                <Trans>Następna</Trans>
              </button>
            </div>
          )}

          {/* Actions */}
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={() => setIsOverLoginModal(false)}
              disabled={isOverLoginPending}>
              <Trans>Anuluj</Trans>
            </button>
          </div>
        </div>
      </WiseModal>
    </div>
  );
};

export default OverLoginUsers;
